import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logEvent } from '@amplitude/analytics-browser';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import TopBar from '../../../componentsv2/TopBar';
import MarcoLogo from '../../../componentsv2/MarcoLogo';
import { CrossIcon } from '../../../components/Icons';
import { validateTfa, validateRecoveryCode } from '../../../services/auth';
import {
  setShowTfaPage,
  setShowTfaSetupModal,
  setTfaValidated,
  setUser,
  useAppDispatch,
  useAppSelector,
} from '../../../store';
import { getRedirectPage, getUrlParams } from '../../../utils/helpers';
import { useTranslation } from '../../../hooks/i18n';
import {
  ACCESS_TOKEN_STORAGE_KEY,
  BANKING,
  DASHBOARD,
  REFRESH_TOKEN_STORAGE_KEY,
  TFA_VALIDATED_STORAGE_KEY,
  TRUSTED_DEVICE_TOKEN_STORAGE_KEY,
} from '../../../constants/auth';
import { twoFactorEvents } from '../../../constants/events';
import { Nullable } from '../../../types';
import Step1TwoFactorCode from './components/Step1TwoFactorCode';
import Step2AlternativeMethods from './components/Step2AlternativeMethods';
import Step3RecoveryCode from './components/Step3RecoveryCode';
import Step4GetHelpFromMarco from './components/Step4GetHelpFromMarco';

const TwoFactorAuthentication = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState<Nullable<string>>(null);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleTwoFactorSubmit = async (code: string) => {
    logEvent(twoFactorEvents.TWO_FACTOR_ATTEMPT);

    try {
      setIsLoading(true);
      const response = await validateTfa(code, rememberDevice);

      if (!response.isValid) {
        throw new Error('Invalid 2FA code');
      }

      if (response.tokens) {
        localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, response.tokens.access);
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, response.tokens.refresh);
        localStorage.setItem(TFA_VALIDATED_STORAGE_KEY, response.isValid.toString());
        dispatch(setTfaValidated(response.isValid));
      }

      if (response.trustedDeviceToken) {
        localStorage.setItem(TRUSTED_DEVICE_TOKEN_STORAGE_KEY, response.trustedDeviceToken);
      }

      logEvent(twoFactorEvents.TWO_FACTOR_SUCCESS);
      dispatch(setShowTfaPage(false));
      dispatch(setShowTfaSetupModal(false));
      const urlParams = getUrlParams();
      navigate(getRedirectPage(urlParams) || '/');
    } catch (error) {
      logEvent(twoFactorEvents.TWO_FACTOR_ERROR, { Error: error });
      setError(t('common:twoFactor.verificationError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecoveryCodeSubmit = async (code: string) => {
    logEvent(twoFactorEvents.RECOVERY_CODE_ATTEMPT);

    try {
      setIsLoading(true);
      const response = await validateRecoveryCode(code);

      if (!response.isValid) {
        throw new Error('Invalid recovery code');
      }

      logEvent(twoFactorEvents.RECOVERY_CODE_SUCCESS);
      dispatch(setShowTfaPage(false));
      dispatch(setUser({ ...user, tfaEnabled: false }));
      const urlParams = getUrlParams();
      navigate(getRedirectPage(urlParams) || '/');
    } catch (error) {
      logEvent(twoFactorEvents.RECOVERY_CODE_ERROR, { Error: error });
      setError(t('common:twoFactor.recoveryCodeError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    logEvent(twoFactorEvents.TWO_FACTOR_CANCEL);
    dispatch(setShowTfaPage(false));
    const urlParams = getUrlParams();
    // This is a workaround to redirect to the dashboard if the user is redirected to the 2FA page from the banking page
    if (getRedirectPage(urlParams) === BANKING) {
      navigate(DASHBOARD);
    } else {
      navigate(getRedirectPage(urlParams) || '/');
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1TwoFactorCode
            onContinue={handleTwoFactorSubmit}
            onChooseDifferent={() => setCurrentStep(2)}
            rememberDevice={rememberDevice}
            setRememberDevice={setRememberDevice}
            error={error}
            setError={setError}
            isLoading={isLoading}
          />
        );
      case 2:
        return (
          <Step2AlternativeMethods
            onBack={() => setCurrentStep(1)}
            onSelectRecovery={() => setCurrentStep(3)}
            onSelectGetHelpFromMarco={() => setCurrentStep(4)}
          />
        );
      case 3:
        return (
          <Step3RecoveryCode
            onBack={() => setCurrentStep(2)}
            onSubmit={handleRecoveryCodeSubmit}
            error={error}
            setError={setError}
            isLoading={isLoading}
          />
        );
      case 4:
        return <Step4GetHelpFromMarco />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <TopBar>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ width: '24px' }} />
          <IconButton href="/">
            <MarcoLogo />
          </IconButton>
          <Box sx={{ width: '24px' }}>
            <IconButton
              onClick={handleClose}
              sx={{
                '& svg': {
                  transform: { xs: 'translateX(-1em)', md: 'translateX(-2.5em)' },
                },
              }}
            >
              <CrossIcon />
            </IconButton>
          </Box>
        </Box>
      </TopBar>
      <Container
        sx={{
          height: 'calc(100vh - 67px)',
          display: 'flex',
          alignItems: 'center',
          paddingTop: { xs: '6rem', md: '120px' },
          justifyContent: 'space-between',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: { xs: '100%', lg: '60%' },
          }}
        >
          {renderStep()}
        </Box>
      </Container>
    </Box>
  );
};

export default TwoFactorAuthentication;
